<template>
  <div id="toast-wrapper" class="fixed z-50">
    <TheToast to="#toast-wrapper" />
  </div>

  <ClientOnly>
    <ErrorOwners :owners="['bot-squad-lifecycle-front']">
      <LeadCollectionPopup v-if="canAttachLeadCollection" />
    </ErrorOwners>
  </ClientOnly>

  <!-- The whole page (sticky header included) is pushed left to make room for
       the assistant side panel when it squeezes the layout (open + ≥md). The
       margin width mirrors ASSISTANT_PANEL_WIDTH_PX so host and layout can't
       drift; the transition is timed to match the panel's slide-in. Below md
       the panel is full-screen, so no margin. Also gated on
       isShoppingAssistantWebEnabled: the store's `isOpen` flag persists across
       navigation (so the chat survives moving between enabled routes), but the
       host unmounts on routes that opt out (e.g. /help), so the margin must
       drop with it rather than follow the stale store flag. -->
  <div
    class="transition-[margin-right] duration-[250ms] ease-out"
    :style="{
      marginRight:
        isSqueezing && isShoppingAssistantWebEnabled
          ? `${ASSISTANT_PANEL_WIDTH_PX}px`
          : '0px',
    }"
  >
    <slot name="header">
      <ErrorOwners :owners="['bot-squad-search-and-recommendation-front']">
        <Transition enter-from-class="opacity-0" leave-to-class="opacity-0">
          <RevBackdrop
            v-if="isMenuActive || isSearchBarActive"
            class="z-10 hidden backdrop-blur-2xl transition-opacity duration-200 md:block"
          />
        </Transition>

        <TheHeader />

        <ClientOnly>
          <AddToCartPopover />
        </ClientOnly>
      </ErrorOwners>
    </slot>

    <slot />

    <slot name="footer">
      <ErrorOwners :owners="['bot-squad-search-and-recommendation-front']">
        <Footer />
      </ErrorOwners>
    </slot>
  </div>

  <!-- Rendered last so the side panel (z-10) paints above the page header
       (TheHeader, also z-10) at equal z-index, while staying below the DS modals
       and toasts, which are teleported to <body> / sit at z-50. -->
  <ClientOnly>
    <ShoppingAssistantErrorBoundary>
      <WebSidebarShoppingAssistantHost v-if="isShoppingAssistantWebEnabled" />
      <WebSidebarShoppingAssistantFAB v-if="isShoppingAssistantWebEnabled" />
    </ShoppingAssistantErrorBoundary>
  </ClientOnly>

  <ClientOnly>
    <ErrorOwners :owners="['bot-squad-search-and-recommendation-front']">
      <LiveShoppingWidget
        v-if="isLiveShoppingEnabled && isYoutubeUserConsentGiven"
      />
    </ErrorOwners>
  </ClientOnly>
</template>

<script lang="ts" setup>
import { useHead } from '#imports'
import { watchEffect } from 'vue'

import AddToCartPopover from '@backmarket/nuxt-layer-recommendation/AddToCartPopover.vue'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import ErrorOwners from '@backmarket/nuxt-module-logger/components/ErrorOwners.vue'
import TheToast from '@backmarket/nuxt-module-toast/TheToast.vue'
import { useDataConsent } from '@backmarket/nuxt-module-tracking/useDataConsent'
import { RevBackdrop } from '@ds/components/Backdrop'

import Footer from '~/scopes/footer/components/AppFooter.vue'
import LiveShoppingWidget from '~/scopes/live-shopping/components/LiveShoppingWidget.vue'
import { useIsLiveShoppingEnabled } from '~/scopes/live-shopping/composables/useIsLiveShoppingEnabled'
import TheHeader from '~/scopes/navigation/components/TheHeader/TheHeader.vue'
import { useActiveMenu } from '~/scopes/navigation/composables/useActiveMenu'
import { useToggleSearchBar } from '~/scopes/search-bar/composables/useToggleSearchBar'
import ShoppingAssistantErrorBoundary from '~/scopes/shopping-assistant/components/ShoppingAssistantErrorBoundary/ShoppingAssistantErrorBoundary.vue'
import WebSidebarShoppingAssistantFAB from '~/scopes/shopping-assistant/components/WebSidebarShoppingAssistantFAB/WebSidebarShoppingAssistantFAB.vue'
import WebSidebarShoppingAssistantHost from '~/scopes/shopping-assistant/components/WebSidebarShoppingAssistantHost/WebSidebarShoppingAssistantHost.vue'
import { useIsShoppingAssistantWebEnabled } from '~/scopes/shopping-assistant/composables/useIsShoppingAssistantWebEnabled'
import { useShoppingAssistantSqueezeOffset } from '~/scopes/shopping-assistant/composables/useShoppingAssistantSqueezeOffset'
import { useShoppingAssistantViewport } from '~/scopes/shopping-assistant/composables/useShoppingAssistantViewport'
import { ASSISTANT_PANEL_WIDTH_PX } from '~/scopes/shopping-assistant/constants'

import LeadCollectionPopup from '../scopes/lead-collection/LeadCollectionPopup.vue'
import { useLeadCollectionPopup } from '../scopes/lead-collection/composables/useLeadCollectionPopup'

const { isSearchBarActive } = useToggleSearchBar()
const { canAttachLeadCollection } = useLeadCollectionPopup()
const { isSqueezing } = useShoppingAssistantViewport()

const isShoppingAssistantWebEnabled = useIsShoppingAssistantWebEnabled()
const isLiveShoppingEnabled = useIsLiveShoppingEnabled()
const { isYoutubeUserConsentGiven } = useDataConsent()

// Kept in sync with the marginRight push below so viewport-fixed elements
// (which that margin can't reach) can inset themselves by the same amount.
const { setSqueezeOffsetPx } = useShoppingAssistantSqueezeOffset()
watchEffect(() => {
  setSqueezeOffsetPx(
    isSqueezing.value && isShoppingAssistantWebEnabled.value
      ? ASSISTANT_PANEL_WIDTH_PX
      : 0,
  )
})

const { isMenuActive } = useActiveMenu()
useHead({
  htmlAttrs: {
    lang: useI18nLocale(),
  },
})
</script>
